<template>
  <HrPageLayout
    title="My Payslips"
    subtitle="View and download your payslips"
    icon="💼"
    :breadcrumb-items="[
      { label: 'Dashboard', route: '/employee-dashboard' },
      { label: 'Payslips' }
    ]"
  >
    <!-- Loading State -->
    <div v-if="loading" class="hr-loading-wrap">
      <div class="hr-spinner"></div>
      <p>Loading payslips...</p>
    </div>

    <div v-else-if="payslipsData" class="payslips-content">
      <!-- YTD Summary Cards -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-money"></i></div>
          <p class="hr-metric-label">YTD Gross</p>
          <h2 class="hr-metric-value">KES {{ formatCurrency(payslipsData.ytdSummary.totalGross) }}</h2>
        </div>
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-wallet"></i></div>
          <p class="hr-metric-label">YTD Net</p>
          <h2 class="hr-metric-value">KES {{ formatCurrency(payslipsData.ytdSummary.totalNet) }}</h2>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-minus-circle"></i></div>
          <p class="hr-metric-label">YTD Deductions</p>
          <h2 class="hr-metric-value">KES {{ formatCurrency(payslipsData.ytdSummary.totalDeductions) }}</h2>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-file"></i></div>
          <p class="hr-metric-label">Payslips</p>
          <h2 class="hr-metric-value">{{ payslipsData.ytdSummary.payslipCount }}</h2>
        </div>
      </div>

      <!-- Latest Payslip Highlight -->
      <div v-if="payslipsData.latestPayslip" class="hr-chart-card hr-mb-4 hr-bg-primary-card">
        <div class="hr-chart-body">
          <div class="hr-flex hr-flex-wrap hr-justify-between hr-align-center">
            <div class="hr-flex-1 hr-min-w-200">
              <h5 class="hr-mb-2 hr-text-white">
                <i class="bx bx-star hr-me-2"></i>
                Latest Payslip - {{ payslipsData.latestPayslip.period_label }}
              </h5>
              <div class="hr-flex hr-flex-wrap hr-gap-4">
                <div>
                  <p class="hr-opacity-75 hr-mb-1 hr-small">Gross Salary</p>
                  <h5 class="hr-mb-0 hr-text-white">KES {{ formatCurrency(payslipsData.latestPayslip.gross_salary) }}</h5>
                </div>
                <div>
                  <p class="hr-opacity-75 hr-mb-1 hr-small">Deductions</p>
                  <h5 class="hr-mb-0 hr-text-white">KES {{ formatCurrency(payslipsData.latestPayslip.total_deductions) }}</h5>
                </div>
                <div>
                  <p class="hr-opacity-75 hr-mb-1 hr-small">Net Pay</p>
                  <h5 class="hr-mb-0 hr-text-white">KES {{ formatCurrency(payslipsData.latestPayslip.net_salary) }}</h5>
                </div>
              </div>
            </div>
            <div class="hr-mt-3 hr-mt-md-0">
              <button
                @click="viewPayslip(payslipsData.latestPayslip)"
                class="hr-btn hr-btn-light">
                <i class="bx bx-show hr-me-1"></i>View Details
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-form-grid hr-gap-3">
            <div class="hr-filter-group">
              <label class="hr-form-label">Filter by Year</label>
              <select v-model="filters.year" @change="loadPayslips" class="hr-select">
                <option value="">All Years</option>
                <option v-for="year in payslipsData.availableYears" :key="year" :value="year">{{ year }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Filter by Month</label>
              <select v-model="filters.month" @change="loadPayslips" class="hr-select">
                <option value="">All Months</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
            <div class="hr-filter-group hr-align-end">
              <button @click="resetFilters" class="hr-btn hr-btn-outline-secondary">
                <i class="bx bx-refresh hr-me-1"></i>Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Payslips List -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-file-doc hr-me-2"></i>All Payslips</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="payslipsData.payslips && payslipsData.payslips.length > 0">
            <div class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                <tr>
                  <th>Period</th>
                  <th>Payment Date</th>
                  <th class="hr-text-end">Gross Salary</th>
                  <th class="hr-text-end">Deductions</th>
                  <th class="hr-text-end">Net Pay</th>
                  <th class="hr-text-center">Status</th>
                  <th class="hr-text-center">Actions</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="payslip in payslipsData.payslips" :key="payslip.id">
                  <td>
                    <strong>{{ payslip.period_label }}</strong>
                    <br>
                    <small class="hr-text-muted">
                      {{ formatDate(payslip.payroll_period_start) }} - {{ formatDate(payslip.payroll_period_end) }}
                    </small>
                  </td>
                  <td>
                    <span class="hr-badge hr-badge-info">{{ payslip.payment_date_formatted }}</span>
                  </td>
                  <td class="hr-text-end"><strong>KES {{ formatCurrency(payslip.gross_salary) }}</strong></td>
                  <td class="hr-text-end hr-text-danger">KES {{ formatCurrency(payslip.total_deductions) }}</td>
                  <td class="hr-text-end"><strong class="hr-text-success">KES {{ formatCurrency(payslip.net_salary) }}</strong></td>
                  <td class="hr-text-center">
                    <span :class="getStatusBadgeClass(payslip.payment_status)">
                      {{ payslip.payment_status || 'Paid' }}
                    </span>
                  </td>
                  <td class="hr-text-center">
                    <button
                      @click="viewPayslip(payslip)"
                      class="hr-btn hr-btn-sm hr-btn-outline-primary"
                      title="View Details">
                      <i class="bx bx-show"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="payslipsData.pagination.totalPages > 1" class="hr-pagination-wrap hr-mt-4">
              <div class="hr-pagination-info">
                Showing {{ (payslipsData.pagination.currentPage - 1) * payslipsData.pagination.limit + 1 }}
                to {{ Math.min(payslipsData.pagination.currentPage * payslipsData.pagination.limit, payslipsData.pagination.totalRecords) }}
                of {{ payslipsData.pagination.totalRecords }} payslips
              </div>
              <nav class="hr-pagination">
                <button type="button" class="hr-page-btn" :disabled="payslipsData.pagination.currentPage === 1"
                        @click="changePage(payslipsData.pagination.currentPage - 1)">
                  <i class="bx bx-chevron-left"></i>
                </button>
                <template v-for="page in visiblePages" :key="page">
                  <button type="button" class="hr-page-btn"
                          :class="{ 'hr-page-active': page === payslipsData.pagination.currentPage }"
                          @click="changePage(page)">{{ page }}</button>
                </template>
                <button type="button" class="hr-page-btn"
                        :disabled="payslipsData.pagination.currentPage === payslipsData.pagination.totalPages"
                        @click="changePage(payslipsData.pagination.currentPage + 1)">
                  <i class="bx bx-chevron-right"></i>
                </button>
              </nav>
            </div>
          </div>

          <div v-else class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-file"></i></div>
            <h5>No payslips found</h5>
            <p class="hr-text-muted">Payslips will appear here once payroll is processed</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Payslip Details Modal -->
    <Payslip
      ref="payslipModal"
      :transaction-id="selectedPayslip ? selectedPayslip.id : null"
    />
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import Payslip from "@/components/payroll/Payslip.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "EmployeePayrollPayslips",
  components: { HrPageLayout, Payslip },

  data() {
    return {
      loading: false,
      payslipsData: null,
      selectedPayslip: null,
      filters: {
        year: new Date().getFullYear(),
        month: '',
        page: 1
      }
    };
  },

  computed: {
    visiblePages() {
      if (!this.payslipsData || !this.payslipsData.pagination) return [];
      const current = this.payslipsData.pagination.currentPage;
      const total = this.payslipsData.pagination.totalPages;
      const pages = [];
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },

  mounted() {
    this.loadPayslips();
  },

  methods: {
    async loadPayslips() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append('function', 'getEmployeePayslips');
        if (this.filters.year) data.append('year', this.filters.year);
        if (this.filters.month) data.append('month', this.filters.month);
        data.append('page', this.filters.page);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.payslipsData = res.data.data;
        } else {
          throw new Error(res.data?.message || 'Failed to load payslips');
        }
      } catch (error) {
        this.$toast?.error("Failed to load payslips: " + error.message) || console.error("Failed to load payslips:", error);
      } finally {
        this.loading = false;
      }
    },

    viewPayslip(payslip) {
      this.selectedPayslip = payslip;
      this.$nextTick(() => {
        if (this.$refs.payslipModal) {
          this.$refs.payslipModal.showModal(payslip.id);
        }
      });
    },

    resetFilters() {
      this.filters.year = new Date().getFullYear();
      this.filters.month = '';
      this.filters.page = 1;
      this.loadPayslips();
    },

    changePage(page) {
      if (page < 1 || page > this.payslipsData.pagination.totalPages) return;
      this.filters.page = page;
      this.loadPayslips();
    },

    formatCurrency(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    getStatusBadgeClass(status) {
      const map = { 'Paid': 'hr-badge hr-badge-success', 'Pending': 'hr-badge hr-badge-warning', 'Processing': 'hr-badge hr-badge-info', 'Failed': 'hr-badge hr-badge-danger' };
      return map[status] || 'hr-badge hr-badge-success';
    },

    gotTo(route) {
      sessionStorage.setItem("selectedSub", route);
      sessionStorage.setItem("currentPage", route);
      this.$router.push(route);
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-bg-primary-card {
  background: linear-gradient(135deg, #059669 0%, #064420 100%);
  color: white;
}
.hr-bg-primary-card .hr-chart-body { color: white; }
.hr-btn-light { background: #fff; color: #059669; border: none; }
.hr-btn-light:hover { background: #f8f9fa; }
.hr-min-w-200 { min-width: 200px; }
.hr-mt-md-0 { margin-top: 0; }
@media (min-width: 768px) { .hr-mt-md-0 { margin-top: 0; } }
@media (max-width: 767px) { .hr-mt-md-0 { margin-top: 12px; } }
.hr-justify-between { justify-content: space-between; }
.hr-align-end { align-self: flex-end; }
.hr-form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); }
</style>
